import { Alert, Box, Card, Divider, FormControlLabel, Grid, IconButton, Snackbar, Switch, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import whiteBackIcon from '../../icons/whiteBackIcon.svg';
import HelpIcon from '@mui/icons-material/Help';
import { useHistory, useParams } from 'react-router';
import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentUser } from '../../shared/sagas/auth/authSlice';
import { Role } from '@inosio/payments-sdk';
import { getUpdatePropertyGroupConfigs, selectPropertyGroupDetailsState, selectSpecificPropertyGroupConfigs } from '../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { PmConfigType } from './pmConfigType';
import debounce from 'lodash.debounce';
import { getPmDetails } from '../onboarding/sagas/pmonboardingSlice';
import { SenderIdentitiesTable } from './components/SenderIdentitiesTable';
import { SenderIdentityForm } from './components/SenderIdentityForm';

export const CommunicationConfigSettings = () => {
  //variables
  const { uuid: propertyGroupIdParam } = useParams<{ uuid: string }>();
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();
  const {
    selectedPropertyGroupAssignedConfigs,
    isFetchingAllPropertyGroupConfigsSuccess,
    isUpdatingPropertyGroupConfigsSuccess,
    UpdatingPropertyGroupConfigsError,
  } = useSelector(selectPropertyGroupDetailsState);

  //local states
  const [pmConfig, setPmConfig] = useState<PmConfigType>({});
  const [pendingConfig, setPendingConfig] = useState<any>(null);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarErrorMessage, setSnackBarErrorMessage] = useState('');
  const currentUser = useSelector(selectCurrentUser);

  const handleChange = (ConfigTypeKey1: string, AssignedValue1: any, ConfigTypeKey2?: string, AssignedValue2?: any) => {
    let newPmConfig = {};

    if (ConfigTypeKey2 && AssignedValue2 !== undefined) {
      newPmConfig = {
        ...pmConfig,
        [ConfigTypeKey1]: AssignedValue1,
        [ConfigTypeKey2]: AssignedValue2,
      };
      setPendingConfig({
        ...pendingConfig,
        [ConfigTypeKey1]: AssignedValue1,
        [ConfigTypeKey2]: AssignedValue2,
      });
    } else {
      newPmConfig = {
        ...pmConfig,
        [ConfigTypeKey1]: AssignedValue1,
      };
      setPendingConfig({
        ...pendingConfig,
        [ConfigTypeKey1]: AssignedValue1,
      });
    }

    setPmConfig(newPmConfig);
  };

  //useffects
  useEffect(() => {
    setPmConfig(selectedPropertyGroupAssignedConfigs);
  }, [selectedPropertyGroupAssignedConfigs]);

  useEffect(() => {
    dispatch({
      type: selectSpecificPropertyGroupConfigs.type,
      payload: {
        propertyGroupUuid: propertyGroupIdParam,
      },
    });
  }, [isFetchingAllPropertyGroupConfigsSuccess]);

  const actuallyDispatchConfigUpdate = () => {
    if (pendingConfig) {
      dispatch(getUpdatePropertyGroupConfigs({ propertyGroupUuid: propertyGroupIdParam, productTypeId: 8, updatedPmConfig: pendingConfig }));
      setPendingConfig(null);
    }
  };

  const delayedConfigUpdate = useCallback(debounce(actuallyDispatchConfigUpdate, 750), [pendingConfig]);

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackBarOpen(false);
  };

  useEffect(() => {
    delayedConfigUpdate();
    return delayedConfigUpdate.cancel;
  }, [pendingConfig, delayedConfigUpdate]);

  //fetching pm details on successful update bcz it has the features enabled data which can be impacted by this config update
  useEffect(() => {
    if (isUpdatingPropertyGroupConfigsSuccess) {
      dispatch(getPmDetails());
    }
  }, [isUpdatingPropertyGroupConfigsSuccess]);

  useEffect(() => {
    if (UpdatingPropertyGroupConfigsError.message?.length > 0) {
      setSnackBarOpen(true);
      setSnackBarErrorMessage(UpdatingPropertyGroupConfigsError.message);
    } else {
      setSnackBarErrorMessage('');
    }
  }, [UpdatingPropertyGroupConfigsError]);

  useEffect(() => {
    if (UpdatingPropertyGroupConfigsError.message?.length > 0) {
      setSnackBarOpen(true);
      setSnackBarErrorMessage(UpdatingPropertyGroupConfigsError.message);
    } else {
      setSnackBarErrorMessage('');
    }
  }, [UpdatingPropertyGroupConfigsError]);


  const getTypography = (text: string, fontWeight: number, fontSize: string) => (
    <Typography
      sx={{
        fontFamily: 'Work Sans',
        fontWeight: { fontWeight },
        fontSize: { fontSize },
        lineHeight: '22px',
      }}
    >
      {text}
    </Typography>
  );
  return (
    <Grid item xs={12} sx={{ mt: 3.5 }}>
      <Card sx={{ display: 'flex', flexDirection: 'column', color: '#000', minHeight: 'calc( 100vh - 144px )' }}>
        <Box className={classes.headerContainer} sx={{ backgroundColor: '#38968d' }}>
          <IconButton
            sx={{ marginRight: '10px', marginTop: '-4px' }}
            onClick={() => {
              history.goBack();
            }}
          >
            <img src={whiteBackIcon} alt="" height="14px" />
          </IconButton>
          <FormControlLabel
            sx={{ color: '#FFF', ml: 0 }}
            control={
              <Switch
                checked={!!pmConfig.CommunicationsProductEnabled}
                onChange={(event) => handleChange(event.target.name, event.target.checked)}
                name="CommunicationsProductEnabled"
                disabled={currentUser?.currentProfile.role === Role.PropertyManager}
              />
            }
            label={getTypography('Communications', 600, '24px')}
            labelPlacement="start"
          />
        </Box>
        <Grid container sx={{ p: 2, pt: 2, pb: 1, position: 'relative' }}>
          <Grid item xs={12} md={12}>
            <Box className={classes.configContainer}>
              <Box className={classes.configHeaderContainer} sx={{ justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {getTypography('Sender Identities', 600, '18px')}

                  <Tooltip title="Sender Identites" placement="right">
                    <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
                  </Tooltip>
                </Box>
              </Box>
              <Box
                className={classes.configBodyContainer}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  marginTop: '1px',
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: 7,
                  },
                  '&::-webkit-scrollbar-track': {
                    boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                    borderRadius: '24px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'darkgrey',
                    borderRadius: '24px',
                  },
                  mb: 1,
                }}
              >
                <SenderIdentityForm />
                <Divider style={{ border: '0.5px solid #F1F0F5' }} sx={{ mx: -2 }} />
                <Grid item gap={5} sx={{ marginTop: '10px' }}>
                  {getTypography('Active Sender Identities:', 400, '15px')}
                  <SenderIdentitiesTable />
                </Grid>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Card>
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={snackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="error">
          {snackBarErrorMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export const useStyles = makeStyles((theme) => ({
  headerContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: '48px',
    backgroundColor: '#053742',
    padding: '0 13px',
  },
  configContainer: {
    border: '3px solid #F1F0F5',
    borderRadius: '24px',
    margin: '16px 8px',
  },
  configHeaderContainer: {
    display: 'flex',
    alignItems: 'center',
    height: '47px',
    borderBottom: '3px solid #F1F0F5',
    padding: '0 18px',
  },
  configBodyContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0 18px',
  },
}));
